import { motion } from "framer-motion";

const TEAM = [
  {
    name: "Aishwary Tandon",
    image: "/team/aishwary.jpg",
    companies: ["Anupam Steels", "Steeldalal Pvt. Ltd."],
    total: 7,
    metal: 7,
  },
  {
    name: "Shubham Palit",
    image: "/team/shubham.jpg",
    companies: ["Citibank NA", "Reliance Capital", "Axis Bank", "Tirupati Structures", "GND Ispat", "Metalbook"],
    total: 13,
    metal: 11,
  },
  {
    name: "Sumit Singh",
    image: "/team/sumit.jpg",
    companies: ["Volkswagen India", "Neelkanth Steel & Alloys", "APL Apollo Tubes", "OFB Tech", "Metalbook"],
    total: 16,
    metal: 12,
  },
  {
    name: "Hemant Kumar Sinha",
    image: "/team/hemant.jpg",
    companies: ["Patra India", "Tirupati Structures", "Metalbook"],
    total: 9,
    metal: 8,
  },
  {
    name: "Aakash Mozumdar",
    image: "/team/aakash.jpg",
    companies: ["Hero FinCorp", "Metalbook"],
    total: 7,
    metal: 5,
  },
];

const slug = (name) => name.toLowerCase().replace(/\s/g, "-");

function TeamCard({ member, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="team-card rounded-xl border border-ib-steel/20 bg-ib-surface/50 overflow-hidden group border-t-2 border-t-ib-blue flex flex-col"
      data-testid={`team-member-${slug(member.name)}`}
    >
      <div className="aspect-square overflow-hidden bg-ib-steel/10 relative m-2.5 mb-0 rounded-lg">
        <img
          src={member.image}
          alt={member.name}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          data-testid={`team-photo-${slug(member.name)}`}
        />
      </div>

      <div className="p-3 flex flex-col flex-1">
        <h3 className="font-outfit font-bold text-sm text-ib-text leading-tight" data-testid={`team-name-${slug(member.name)}`}>
          {member.name}
        </h3>
        <div className="flex flex-wrap gap-1 mt-2.5">
          {member.companies.map((c) => (
            <span
              key={c}
              className="font-jetbrains text-[8px] px-1.5 py-0.5 rounded border border-ib-steel/30 text-ib-text-secondary"
            >
              {c}
            </span>
          ))}
          <span className="font-jetbrains text-[8px] px-1.5 py-0.5 rounded border border-ib-blue/50 text-ib-blue bg-ib-blue/10">
            Iron Byte
          </span>
        </div>
        <div className="mt-3" />
        <div className="mt-auto pt-3 border-t border-ib-steel/15 space-y-1">
          <div className="flex justify-between font-manrope text-[10px]">
            <span className="text-ib-text-muted">Total exp.</span>
            <span className="font-jetbrains text-ib-text">{member.total} yrs</span>
          </div>
          <div className="flex justify-between font-manrope text-[10px]">
            <span className="text-ib-text-muted">Metal industry</span>
            <span className="font-jetbrains text-ib-blue">{member.metal} yrs</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function FoundingTeam() {
  return (
    <section id="team" data-testid="team-section" className="py-24 px-6 relative">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-14 max-w-3xl"
        >
          <span className="font-jetbrains text-xs text-ib-lime tracking-widest uppercase">Team</span>
          <h2 className="font-outfit font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-3 text-ib-text">
            Built by people who've <span className="text-ib-blue">lived the problem</span>, not just studied it.
          </h2>
          <p className="font-manrope text-base md:text-lg text-ib-text-secondary mt-4">
            <span className="font-unbounded font-black text-ib-text">Iron</span>
            <span className="font-jetbrains text-ib-blue">Byte</span> is led by a team with decades of combined
            experience across the metal industry and financial services — the same relationships and market
            knowledge that give us a distribution edge others can't replicate.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {TEAM.map((member, i) => (
            <TeamCard key={member.name} member={member} index={i} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-12 glass rounded-xl p-6 md:p-8 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-center"
          data-testid="team-why-wins"
        >
          <div>
            <h3 className="font-outfit font-bold text-lg text-ib-text mb-2">Why this team wins</h3>
            <p className="font-manrope text-sm text-ib-text-secondary leading-relaxed">
              Every member of the Iron Byte team has spent years operating inside the metal industry itself — not
              observing it from the outside. Across banking, steel manufacturing, structural fabrication, auto
              ancillary, and B2B metal marketplaces, that means faster supplier trust, faster buyer adoption, and a
              product shaped by people who've lived the exact problem Iron Byte is solving.
            </p>
          </div>
          <div className="flex gap-8 lg:gap-10 lg:pl-8 lg:border-l border-ib-steel/20">
            <div>
              <div className="font-jetbrains text-3xl font-bold text-ib-text">52+</div>
              <div className="font-manrope text-xs text-ib-text-muted mt-1">yrs professional<br />experience</div>
            </div>
            <div>
              <div className="font-jetbrains text-3xl font-bold text-ib-blue">43+</div>
              <div className="font-manrope text-xs text-ib-text-muted mt-1">yrs in metal trading<br />& manufacturing</div>
            </div>
          </div>
        </motion.div>

        <p className="mt-10 text-center font-outfit font-semibold text-sm md:text-base text-ib-blue">
          "The right team doesn't just build the product — they know exactly which problem is worth solving. We do."
        </p>
      </div>
    </section>
  );
}
